import { useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Bell, Check, CheckCheck, Megaphone, Pin, Trash2, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { useNotifications } from "@/hooks/useNotifications";
import { resolveVideoEmbed, type AdminAnnouncement } from "@/lib/announcements";
import { formatNotificationTime } from "@/lib/notifications";
import { cn } from "@/lib/utils";

const severityClass: Record<AdminAnnouncement["severity"], string> = {
  info: "bg-primary/10 text-primary",
  success: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  warning: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
};

function formatAnnouncementDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type Props = {
  className?: string;
  iconClassName?: string;
};

export const UnifiedNotificationsBell = ({ className, iconClassName }: Props) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"team" | "system">("team");

  const announcements = useAnnouncements();
  const {
    notifications,
    unreadCount: systemUnread,
    loading: systemLoading,
    markRead,
    markAllRead,
    remove,
  } = useNotifications();

  const teamUnread = announcements.unread.length;
  const totalUnread = teamUnread + systemUnread;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Auto-mark announcements as read when the panel opens (matches prior behavior).
      for (const a of announcements.unread) void announcements.markRead(a.id);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
          aria-label={
            totalUnread > 0
              ? `Notifications (${totalUnread} unread)`
              : "Notifications"
          }
          title="Notifications"
        >
          <Bell className={cn("h-5 w-5", iconClassName)} />
          {totalUnread > 0 && (
            <span
              className="absolute top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
              aria-hidden="true"
            >
              {totalUnread > 9 ? "9+" : totalUnread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" /> Notifications
          </SheetTitle>
        </SheetHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "team" | "system")}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="px-4 pt-3">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="team" className="gap-2">
                <Megaphone className="h-4 w-4" />
                <span className="truncate">Excerptus Updates</span>
                {teamUnread > 0 && (
                  <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[10px]">
                    {teamUnread > 9 ? "9+" : teamUnread}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="system" className="gap-2">
                <Activity className="h-4 w-4" />
                <span className="truncate">System Notifications</span>
                {systemUnread > 0 && (
                  <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[10px]">
                    {systemUnread > 9 ? "9+" : systemUnread}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Excerptus Team Updates */}
          <TabsContent value="team" className="flex-1 min-h-0 mt-0">
            <ScrollArea className="h-full">
              <div className="px-4 py-3 space-y-3">
                {announcements.loading ? (
                  <p className="text-sm text-muted-foreground p-4">Loading…</p>
                ) : announcements.visible.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4 text-center">
                    No announcements right now.
                  </p>
                ) : (
                  announcements.visible.map((a) => {
                    const video = a.video_url ? resolveVideoEmbed(a.video_url) : null;
                    const read = announcements.isRead(a.id);
                    return (
                      <article
                        key={a.id}
                        className={cn(
                          "rounded-lg border p-4 transition-colors",
                          a.is_pinned
                            ? "border-amber-500/40 bg-amber-500/5"
                            : "border-border bg-card",
                          !read && "ring-1 ring-primary/30",
                        )}
                      >
                        <header className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              {a.is_pinned && <Pin className="h-3 w-3 text-amber-500" />}
                              <Badge
                                variant="secondary"
                                className={cn("text-[10px]", severityClass[a.severity])}
                              >
                                {a.severity}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatAnnouncementDate(a.published_at)}
                              </span>
                            </div>
                            <h3 className="font-semibold text-sm leading-snug">{a.title}</h3>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => announcements.dismiss(a.id)}
                            aria-label="Dismiss"
                            title="Dismiss"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </header>
                        {video && (
                          <div className="aspect-video w-full overflow-hidden rounded-md border bg-muted mb-2">
                            {video.kind === "iframe" ? (
                              <iframe
                                src={video.src}
                                className="h-full w-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={a.title}
                              />
                            ) : (
                              <video src={video.src} controls className="h-full w-full" />
                            )}
                          </div>
                        )}
                        {a.body_md && (
                          <p className="text-sm whitespace-pre-wrap text-foreground/90">
                            {a.body_md}
                          </p>
                        )}
                      </article>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* System Notifications */}
          <TabsContent value="system" className="flex-1 min-h-0 mt-0 flex flex-col">
            <div className="flex items-center justify-between border-b px-4 py-2">
              <div className="text-xs text-muted-foreground">
                {systemUnread > 0 ? `${systemUnread} unread` : "All caught up"}
              </div>
              {notifications.length > 0 && systemUnread > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => void markAllRead()}
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="flex-1">
              {systemLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
              ) : notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  You're all caught up.
                </div>
              ) : (
                <ul className="divide-y">
                  {notifications.map((n) => {
                    const unread = !n.read_at;
                    const inner = (
                      <div
                        className={cn(
                          "flex w-full items-start gap-2 px-3 py-2.5 text-left",
                          unread && "bg-primary/5",
                        )}
                      >
                        <div className="mt-1.5 flex-shrink-0">
                          <span
                            className={cn(
                              "block h-2 w-2 rounded-full",
                              unread ? "bg-primary" : "bg-transparent",
                            )}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium leading-snug">{n.title}</div>
                          {n.body && (
                            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                              {n.body}
                            </div>
                          )}
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            {formatNotificationTime(n.created_at)}
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {unread && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              aria-label="Mark as read"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void markRead(n.id);
                              }}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Delete"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              void remove(n.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                    const handleNavigate = () => {
                      if (unread) void markRead(n.id);
                      setOpen(false);
                    };
                    return (
                      <li key={n.id} className="group">
                        {n.link_url ? (
                          <Link
                            to={n.link_url}
                            onClick={handleNavigate}
                            className="block hover:bg-muted/40"
                          >
                            {inner}
                          </Link>
                        ) : (
                          <div className="hover:bg-muted/40">{inner}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
